// Results tab (plan §6.5 item 4): per arm of a voice-engine experiment,
// users as the unit, as on the talk-track Performance tab. Read-only over
// GET /studio/analytics/voice-engines, so any Studio admin sees it. The math
// and its definitions are app/services/voice_engines/results.py's; this file
// only renders (pure logic: results.ts).

import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { getVoiceEngineResults, type VoiceEngineResults } from "../../services/analytics";
import type { EngineName, Overview } from "../../services/voiceEngines";
import { errorText } from "./errors";
import { engineLabel } from "./labels";
import { Callout } from "./parts";
import { policySummary } from "./policy";
import {
  COST_BASIS,
  FIRST_AUDIO_BASIS,
  FIRST_AUDIO_SCALE_MS,
  FIRST_AUDIO_TARGET_MS,
  armColumns,
  callsLink,
  callsSpan,
  firstAudioBar,
  languageLabel,
  livePolicy,
  languageOptions,
  markedColumn,
  metrics,
  notices,
  resultsCsv,
  seconds,
  spanText,
  type ArmColumn,
  type CallsSpan,
} from "./results";

function download(name: string, text: string) {
  const url = URL.createObjectURL(new Blob([text], { type: "text/csv" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
}

function ArmHead({ column, span }: { column: ArmColumn; span: CallsSpan | null }) {
  return (
    <div className="ve-res-head">
      <span className="ve-row" style={{ gap: 6 }}>
        <span className="ve-dot" style={{ background: column.color }} aria-hidden="true" />
        <span className="mono">{column.label}</span>
        {column.pct !== null && <span className="muted">{column.pct}%</span>}
      </span>
      <span className="muted ve-small">
        {column.key === null ? "cascade, outside every arm" : column.engine ? engineLabel(column.engine) : ""}
      </span>
      {span && column.key !== null && column.data && (
        <Link className="ve-small" to={callsLink(span, column.key)}>
          calls →
        </Link>
      )}
    </div>
  );
}

export function ResultsTab({ overview }: { overview: Overview }) {
  const policy = overview.inForce.policy;
  // Off, or the kill switch on: no experiment is live, whatever the value names.
  const live = livePolicy(policy, overview.env.s2sEnabled);
  const liveExperiment = live?.experiment ?? null;
  // null: the server's newest experiment (before the first load).
  const [experiment, setExperiment] = useState<string | null>(liveExperiment);
  const [language, setLanguage] = useState<string | null>(null);
  const [data, setData] = useState<VoiceEngineResults | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getVoiceEngineResults({ experiment: experiment ?? undefined, language: language ?? undefined })
      .then((r) => {
        if (cancelled) return;
        setData(r);
        setError(null);
      })
      .catch((e) => !cancelled && setError(errorText(e)))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [experiment, language]);

  const columns = useMemo(() => (data ? armColumns(data, live) : []), [data, live]);
  const rows = useMemo(() => metrics(data?.longCallSeconds ?? 30), [data]);

  if (!data) {
    return error ? (
      <div className="state-box error">Results unavailable ({error}).</div>
    ) : (
      <div className="state-box">Loading results…</div>
    );
  }

  const shown = data.experiment;
  const isLive = shown !== null && shown === liveExperiment;
  const linkSpan = callsSpan(data);
  const span = spanText(data.firstCallAt, data.lastCallAt);
  const minUsers = data.lowSampleUsers;
  const experimentKeys = [...new Set([...(liveExperiment ? [liveExperiment] : []), ...data.experiments.map((e) => e.key)])];
  const enginesShown = [...new Set(columns.map((c) => c.engine).filter((e): e is EngineName => !!e))];
  const withFallback = columns.filter((c) => c.engine !== "cascade" && c.key !== null);

  return (
    <div className={loading ? "ve-res ve-faded" : "ve-res"} aria-busy={loading}>
      <div className="ve-row ve-res-top">
        <div style={{ flex: "1 1 360px", minWidth: 0 }}>
          <div className="ve-row">
            {experimentKeys.length > 1 ? (
              <select
                aria-label="Experiment"
                className="mono ve-res-exp"
                value={shown ?? ""}
                onChange={(e) => setExperiment(e.target.value || null)}
              >
                {experimentKeys.map((k) => (
                  <option key={k} value={k}>
                    {k}
                    {k === liveExperiment ? " (live)" : ""}
                  </option>
                ))}
              </select>
            ) : (
              <span className="mono ve-res-exp">{shown ?? "no experiment"}</span>
            )}
            {isLive && <span className="badge ve-info">live</span>}
            {span && <span className="badge">{span}</span>}
          </div>
          <div className="muted ve-small" style={{ marginTop: 4 }}>
            {isLive && live && `Arms ${policySummary(live)}, among iOS builds ${live.min_build_ios}+ and Android ${live.min_build_android}+. `}
            Users are the unit; app voice calls the policy bucketed, test users and Keyboard Mode left out. A call
            that fell back stays in its arm.
          </div>
        </div>
        <div className="seg" role="group" aria-label="Language">
          {languageOptions(data, language).map((lang) => (
            <button
              key={lang ?? "all"}
              type="button"
              className={language === lang ? "active" : ""}
              aria-pressed={language === lang}
              onClick={() => setLanguage(lang)}
            >
              {languageLabel(lang)}
            </button>
          ))}
        </div>
        <button
          type="button"
          className="btn"
          disabled={!shown || columns.length === 0}
          onClick={() => download(`voice-engines-${shown}-${language ?? "all"}.csv`, resultsCsv(data, columns))}
        >
          Export CSV
        </button>
      </div>

      {error && <p className="error-text">{error}</p>}
      {notices({
        results: data,
        columns,
        allowedEngines: overview.env.allowedEngines,
        environment: overview.env.environment,
        policy,
        s2sEnabled: overview.env.s2sEnabled,
      }).map((n) => (
        <Callout key={n.badge} tone={n.tone} badge={n.badge}>
          {n.text}
        </Callout>
      ))}

      {columns.length === 0 ? (
        <div className="state-box">
          {shown
            ? `No calls in ${shown} yet${language ? ` in ${languageLabel(language)}` : ""}.`
            : "No voice-engine experiment has had calls yet. Results appear once a policy with an experiment buckets calls."}
        </div>
      ) : (
        <>
          <section className="panel ve-section" aria-labelledby="ve-res-h">
            <h2 id="ve-res-h" className="panel-head">
              By arm · {language ? languageLabel(language) : "all languages"}
            </h2>
            <div className="table-scroll">
              <table className="guide-table ve-res-table">
                <thead>
                  <tr>
                    <th className="no-sort">Metric</th>
                    {columns.map((c) => (
                      <th key={c.key ?? "rest"} className="no-sort">
                        <ArmHead column={c} span={linkSpan} />
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((m) => {
                    const marked = markedColumn(columns, m, minUsers);
                    return (
                      <tr key={m.key}>
                        <th scope="row">
                          <div>{m.label}</div>
                          <div className="ve-help">{m.hint}</div>
                        </th>
                        {columns.map((c, i) => {
                          const cell = m.cell(c.data, c);
                          const cls = cell.danger ? "ve-res-danger" : i === marked ? "ve-res-best" : "";
                          return (
                            <td key={c.key ?? "rest"}>
                              <span className={`ve-num ${cls}`}>{cell.text}</span>
                              {cell.sub && <span className="muted ve-small"> {cell.sub}</span>}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="ve-help" style={{ marginTop: 8 }}>
              Ranges are 95% over users: the mean of per-user values ± 1.96 standard errors. They are not a
              significance test. A value is in bold only when every arm has at least {minUsers} users behind it and
              its range overlaps none of the others'. Calls are conversations: a reconnect is part of the same call.
            </div>
          </section>

          <div className="ve-columns ve-section">
            <section className="panel ve-main" aria-labelledby="ve-ttfa-h">
              <h2 id="ve-ttfa-h" className="panel-head">
                Time to first audio
              </h2>
              <div className="ve-ttfa">
                {columns.map((c) => {
                  const bar = firstAudioBar(c.data?.firstAudio);
                  const fa = c.data?.firstAudio;
                  return (
                    <div key={c.key ?? "rest"}>
                      <div className="ve-row ve-small" style={{ marginBottom: 4 }}>
                        <span className="mono" style={{ fontWeight: 600 }}>{c.label}</span>
                        <span className="muted" style={{ flex: "1 1 auto" }}>{c.engine ? engineLabel(c.engine) : ""}</span>
                        <span>{fa && fa.p50Ms !== null ? `p50 ${seconds(fa.p50Ms)} · p95 ${seconds(fa.p95Ms)}` : "no data"}</span>
                      </div>
                      <div
                        className="ve-ttfa-track"
                        role="img"
                        aria-label={fa && fa.p50Ms !== null ? `${c.label}: p50 ${seconds(fa.p50Ms)}, p95 ${seconds(fa.p95Ms)}` : `${c.label}: no data`}
                      >
                        {bar && (
                          <>
                            <span className="ve-ttfa-p50" style={{ width: `${bar.p50}%`, background: c.color }} />
                            <span className="ve-ttfa-p95" style={{ left: `${bar.p50}%`, width: `${bar.spread}%`, borderColor: c.color }} />
                          </>
                        )}
                        <span className="ve-ttfa-target" style={{ left: `${(FIRST_AUDIO_TARGET_MS / FIRST_AUDIO_SCALE_MS) * 100}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="muted ve-small ve-row" style={{ justifyContent: "space-between", marginTop: 6 }}>
                <span>0 s</span>
                <span>dashed line: the {seconds(FIRST_AUDIO_TARGET_MS)} bar (plan §5.10)</span>
                <span>{seconds(FIRST_AUDIO_SCALE_MS)}</span>
              </div>
              <ul className="ve-help ve-res-notes">
                {enginesShown.map((e) => (
                  <li key={e}>
                    {engineLabel(e)}: {FIRST_AUDIO_BASIS[e]}.
                  </li>
                ))}
                <li>The engines measure from different points, so compare an engine with itself over time first.</li>
              </ul>
            </section>

            <section className="panel ve-aside" aria-labelledby="ve-fb-h">
              <div>
                <h2 id="ve-fb-h" className="panel-head">
                  Fallbacks to the cascade
                </h2>
                {withFallback.length === 0 ? (
                  <div className="muted ve-small">No arm runs an engine that can fall back.</div>
                ) : (
                  withFallback.map((c) => {
                    const fb = c.data?.fallback;
                    const reasons = Object.entries(fb?.reasons ?? {});
                    return (
                      <div key={c.key ?? "rest"} className="ve-list-row" style={{ flexDirection: "column", alignItems: "stretch" }}>
                        <div className="ve-row">
                          <span className="ve-dot" style={{ background: c.color }} aria-hidden="true" />
                          <span className="mono" style={{ fontWeight: 600 }}>{c.label}</span>
                          <span className="muted ve-small">
                            {fb && c.data?.calls ? `${fb.calls} of ${c.data.calls} calls` : "no calls yet"}
                          </span>
                        </div>
                        {reasons.length > 0 && linkSpan && c.key && (
                          <div className="chip-row" style={{ marginTop: 6 }}>
                            {reasons.map(([reason, n]) => (
                              <Link
                                key={reason}
                                className="chip"
                                to={callsLink(linkSpan, c.key as string, reason)}
                                aria-label={`${reason}: ${n} sockets, open the calls`}
                                title={language ? "Opens the calls in every language: the Calls page has no language filter" : "Opens these calls"}
                              >
                                <span className="mono">{reason}</span> {n}
                              </Link>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
                <div className="ve-help" style={{ marginTop: 8 }}>
                  A fallback finishes the call on the cascade. Counts are sockets per reason; each opens those calls.
                </div>
              </div>
              <div>
                <h2 className="panel-head">Cost per minute</h2>
                <ul className="ve-help ve-res-notes">
                  {enginesShown.map((e) => (
                    <li key={e}>
                      {engineLabel(e)}: {COST_BASIS[e]}.
                    </li>
                  ))}
                  <li>Pooled over the arm's minutes. A call that fell back is costed over its whole length.</li>
                </ul>
              </div>
            </section>
          </div>
        </>
      )}
    </div>
  );
}
